import { isManaged, resolveModulePath, type ProjectTree } from '@gotti/sandbox-core';

/** Script files are type-checked together, so the editor keeps a model for each; any other file gets one when opened. */
export const isScriptPath = (path: string): boolean => /\.tsx?$/.test(path);

/** The Monaco language a file opens in, by its extension. */
export function languageOf(path: string): string {
  if (/\.tsx?$/.test(path)) return 'typescript';
  if (/\.(?:[cm]?js|jsx)$/.test(path)) return 'javascript';
  if (path.endsWith('.json')) return 'json';
  if (path.endsWith('.html')) return 'html';
  if (path.endsWith('.css')) return 'css';
  if (path.endsWith('.md')) return 'markdown';
  return 'plaintext';
}

/** The folder a path sits in (`''` for a root file). */
export const folderOf = (path: string): string => (path.includes('/') ? path.slice(0, path.lastIndexOf('/')) : '');

/** A path's last segment: the file's name with its extension. */
export const baseNameOf = (path: string): string => path.slice(path.lastIndexOf('/') + 1);

/** Which side's folder a path is under (`src/<side>/…`); anything else counts as shared. */
export const sideOf = (path: string): 'client' | 'server' | 'shared' => {
  const side = /^src\/(client|server|shared)\//.exec(path)?.[1];
  return (side as 'client' | 'server' | 'shared' | undefined) ?? 'shared';
};

/** Every folder a path sits in, outermost first: `src/client/systems/X.ts` → `src`, `src/client`, `src/client/systems`. */
export const foldersOf = (path: string): string[] => {
  const segments = path.split('/');
  return segments.slice(0, -1).map((_, i) => segments.slice(0, i + 1).join('/'));
};

/**
 * Whether a typed name ends in a file type, so it keeps the extension as typed (`levels.json`). Any other suffix is part
 * of the name — `Movement.spec` is the test file `Movement.spec.ts`, as the editor always made it.
 */
export const hasFileType = (name: string): boolean => /\.(?:tsx?|[cm]?js|jsx|json|html|css|md|txt|svg|ya?ml|xml|csv)$/i.test(name);

/** A spec or test file: what the Tests panel runs, and the file tree leaves out. */
export const isTestPath = (path: string): boolean => /\.(spec|test)\.tsx?$/.test(path);

/**
 * A name a person types for a new or renamed file or folder: letters, digits, `_`, `-` and dots, no folder, no leading or
 * trailing dot. The rule lives in @gotti/sandbox-core, where the project operations hold every new name to it.
 */
export { isValidFileName } from '@gotti/sandbox-core';

/**
 * `name` in `folder` (`''` for the project root) — or, when a file there already has it in any letter case, the same
 * name with the next free number before its extension: `Input.ts`, `Input1.ts`, `Input2.ts`, as the editor always did.
 */
export function uniqueFilePath(tree: ProjectTree, folder: string, name: string): string {
  const taken = new Set(Object.keys(tree.files).map((path) => path.toLowerCase()));
  const at = (candidate: string) => (folder ? `${folder}/${candidate}` : candidate);
  if (!taken.has(at(name).toLowerCase())) return at(name);
  const dot = name.lastIndexOf('.');
  const [stem, extension] = dot > 0 ? [name.slice(0, dot), name.slice(dot)] : [name, ''];
  for (let n = 1; ; n++) {
    const candidate = at(`${stem}${n}${extension}`);
    if (!taken.has(candidate.toLowerCase())) return candidate;
  }
}

/** The code a new file starts with, by the folder it is created in — the editor's templates, as before. */
export function generateNewFileCodeForPath(path: string): string {
  if (!isScriptPath(path)) return '';
  const className = sanitizeClassName(baseNameOf(path).replace(/\.tsx?$/, ''));
  const segments = path.split('/');
  const side = segments[0] === 'src' && ['client', 'server', 'shared'].includes(segments[1]) ? segments[1] : null;
  const parent = segments.at(-2);
  if (parent === 'components') {
    return `import { Component } from '@gotti/core';\n\nexport class ${className} extends Component {\n  constructor() {\n    super('${className}');\n  }\n}\n`;
  }
  if (parent === 'entities') {
    return `import { Entity } from '@gotti/core';\n\nexport type ${className}InitOptions = any;\n\nexport class ${className} extends Entity<${className}InitOptions> {\n  constructor() {\n    super('${className}');\n  }\n}\n`;
  }
  if (parent === 'systems' && side !== null) {
    return side === 'client'
      ? `import { ClientSystem } from '@gotti/client';\n\nexport class ${className} extends ClientSystem() {\n  constructor() {\n    super('${className}');\n  }\n}\n`
      : `import { ServerSystem } from '@gotti/server';\n\nexport class ${className} extends ServerSystem() {\n  constructor() {\n    super('${className}');\n  }\n}\n`;
  }
  if (side !== null && segments.length === 3) {
    return `// ${className}.ts\n// This file is at the root of the ${side} folder\n\nexport {};\n`;
  }
  return `// ${className}.ts\n\nexport {};\n`;
}

/** An importer's code before and after a rename rewrote its imports. */
export interface ImportEdit {
  path: string;
  previous: string;
  next: string;
}

const IMPORT_SPECIFIER = /(from\s+['"])([^'"]+)(['"])/g;
const withoutScriptExtension = (path: string): string => path.replace(/\.tsx?$/, '');
const escapeRegExp = (text: string): string => text.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

/** Whether `specifier`, written in `importer`, names `target` — by a relative path or `@/`, with or without its extension. */
const names = (importer: string, specifier: string, target: string): boolean =>
  withoutScriptExtension(resolveModulePath(`/${importer}`, specifier, '/src/')) === `/${withoutScriptExtension(target)}`;

/** The script files whose imports name `target`, in tree order — the registries aside: the site rewrites those itself. */
export function importersOf(tree: ProjectTree, target: string): string[] {
  return Object.entries(tree.files)
    .filter(([path, code]) =>
      path !== target && isScriptPath(path) && !isManaged(tree, path) &&
      [...code.matchAll(IMPORT_SPECIFIER)].some((match) => names(path, match[2], target)))
    .map(([path]) => path);
}

/** For a rename of `target` to `renamed` in the same folder: each importer's code with those imports pointing at the new name. */
export function importEditsForRename(tree: ProjectTree, target: string, renamed: string): ImportEdit[] {
  const from = new RegExp(`(^|/)${escapeRegExp(withoutScriptExtension(baseNameOf(target)))}(\\.tsx?)?$`);
  const to = withoutScriptExtension(baseNameOf(renamed));
  return importersOf(tree, target).map((path) => {
    const previous = tree.files[path];
    const next = previous.replace(IMPORT_SPECIFIER, (match, open: string, specifier: string, close: string) =>
      names(path, specifier, target) ? `${open}${specifier.replace(from, `$1${to}$2`)}${close}` : match);
    return { path, previous, next };
  });
}

/** The file the editor shows after a load: a person's first client system, else their first client file, shared root file, any file of theirs. */
export function firstFileToOpen(tree: ProjectTree): string | null {
  const own = Object.keys(tree.files).filter((path) => !tree.generated.has(path) && !/\.(spec|test)\.tsx?$/.test(path));
  return (
    own.find((path) => path.startsWith('src/client/systems/')) ??
    own.find((path) => path.startsWith('src/client/')) ??
    own.find((path) => /^src\/shared\/[^/]+$/.test(path)) ??
    own[0] ??
    Object.keys(tree.files).find(isScriptPath) ??
    null
  );
}

/** The folders the file tree opens with: each side, and its systems, components and entities — as before. */
export const DEFAULT_EXPANDED_FOLDERS: readonly string[] = [
  'src/client', 'src/server', 'src/shared',
  'src/client/systems', 'src/client/components', 'src/client/entities',
  'src/server/systems', 'src/server/components', 'src/server/entities',
  'src/shared/components', 'src/shared/entities',
];

/**
 * Sanitize a file name to be a valid JavaScript class name.
 * - Strips .ts extension if present
 * - Removes illegal characters (keeps only alphanumeric and underscore)
 * - Ensures it starts with a letter (prepends underscore if needed)
 */
export function sanitizeClassName(name: string): string {
  // Strip .ts or .tsx extension if present
  let sanitized = name.replace(/\.(tsx?)$/i, '');
  
  // Preserve .spec / .test suffixes used for test files
  const specMatch = sanitized.match(/\.(spec|test)$/i);
  const specSuffix = specMatch ? specMatch[0] : '';
  if (specSuffix) {
    sanitized = sanitized.slice(0, -specSuffix.length);
  }
  
  // Remove any characters that aren't alphanumeric or underscore
  sanitized = sanitized.replace(/[^a-zA-Z0-9_]/g, '');
  
  // Re-append the spec/test suffix
  sanitized = sanitized + specSuffix;
  
  // If empty after sanitization, use a default
  if (!sanitized) {
    return 'Untitled';
  }
  
  // If starts with a number, prepend underscore
  if (/^[0-9]/.test(sanitized)) {
    sanitized = '_' + sanitized;
  }
  
  return sanitized;
}
